import type { ReactNode } from 'react'
import { Card, CardContent } from '@/ui/design-system'
import { Badge } from '@/ui/design-system'
import { cn } from '@/shared/utils'

export interface SectionCardProps {
  /** Optional icon in header; when omitted, header is title + badge only for a cleaner look */
  icon?: ReactNode
  /** Title; can be a string or a fragment (e.g. title + tooltip trigger) */
  title: ReactNode
  /** Optional count for the badge; when undefined, no badge is shown */
  count?: number
  /** Optional actions (e.g. buttons) rendered in the header row, right-aligned */
  actions?: ReactNode
  children: ReactNode
  className?: string
  /** Optional className for the card content area */
  contentClassName?: string
  /** When true, card fills container and content area scrolls (for fixed-height section grid) */
  fixedHeight?: boolean
}

/**
 * Section card for trip detail center content.
 * Header: optional icon + title + count badge. Body: CardContent.
 */
export function SectionCard({
  icon,
  title,
  count,
  actions,
  children,
  className,
  contentClassName,
  fixedHeight = false
}: SectionCardProps) {
  return (
    <Card
      className={cn(
        'overflow-hidden',
        fixedHeight && 'flex flex-col flex-1 min-h-0',
        className
      )}
    >
      <div className={cn('flex flex-row items-center gap-3 px-6 pt-6 pb-0', fixedHeight && 'flex-shrink-0')}>
        {icon != null && (
          <div
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-adventure-100 text-adventure-600 dark:bg-adventure-900/40 dark:text-adventure-400"
            aria-hidden
          >
            {icon}
          </div>
        )}
        <h2 className="flex-1 min-w-0 flex items-center gap-1.5 text-lg font-semibold text-earth-900 dark:text-foreground">
          {title}
        </h2>
        {count !== undefined && (
          <Badge
            variant="secondary"
            className="flex-shrink-0 bg-earth-200 text-earth-800 dark:bg-earth-700 dark:text-earth-200"
          >
            {count}
          </Badge>
        )}
        {actions != null && <div className="flex-shrink-0">{actions}</div>}
      </div>
      <CardContent
        className={cn(
          'pt-4',
          fixedHeight && 'flex-1 min-h-0 overflow-y-auto',
          contentClassName
        )}
      >
        {children}
      </CardContent>
    </Card>
  )
}
